import { CalendarClock, Minus, Plus } from 'lucide-react';
import { SegmentedControl, cn } from '@ai-tutor/ui';
import {
  FOCUS_PRESETS,
  FOCUS_STEP_MINUTES,
  LONG_BREAK_RANGE,
  MAX_FOCUS_MINUTES,
  MAX_ROUNDS,
  MIN_FOCUS_MINUTES,
  MIN_ROUNDS,
  SHORT_BREAK_RANGE,
  sessionPlan,
  type FocusMode,
} from '@/lib/focus/focusMachine';
import { formatEndTime, formatMinutes } from '@/lib/focus/format';

export interface PlanChoice {
  mode: FocusMode;
  minutes: number;
  rounds: number;
  /** `null`: follow the focus length (auto). */
  shortMinutes: number | null;
  longMinutes: number | null;
}

interface SessionPlanPanelProps {
  plan: PlanChoice;
  onChange: (patch: Partial<PlanChoice>) => void;
  /** For the end time. */
  now: number;
}

/** "− 25 min +": a value always on show, with its own label for assistive tech. */
function Stepper({
  label,
  value,
  unit,
  min,
  max,
  step,
  disabled = false,
  onChange,
}: {
  label: string;
  value: number;
  unit?: string;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  const btn =
    'tap-target flex h-7 w-7 items-center justify-center rounded-md text-foreground transition-colors duration-fast hover:bg-secondary disabled:pointer-events-none disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  return (
    <div
      role='group'
      aria-label={label}
      className='flex items-center gap-0.5 rounded-lg border border-border p-0.5'
    >
      <button
        type='button'
        className={btn}
        aria-label={`${label}: less`}
        disabled={disabled || value <= min}
        onClick={() => onChange(Math.max(min, value - step))}
      >
        <Minus className='h-3.5 w-3.5' aria-hidden='true' />
      </button>
      <output
        aria-live='polite'
        className='min-w-[4rem] text-center font-mono text-[13px] font-semibold tabular-nums text-foreground'
      >
        {value}
        {unit}
      </output>
      <button
        type='button'
        className={btn}
        aria-label={`${label}: more`}
        disabled={disabled || value >= max}
        onClick={() => onChange(Math.min(max, value + step))}
      >
        <Plus className='h-3.5 w-3.5' aria-hidden='true' />
      </button>
    </div>
  );
}

function Row({
  label,
  hint,
  off = false,
  children,
}: {
  label: string;
  hint: React.ReactNode;
  off?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className='flex min-h-[2.75rem] items-center justify-between gap-3 border-t border-border/70 py-1 first:border-t-0'>
      <div className='min-w-0'>
        <p
          className={cn(
            'text-sm font-medium',
            off ? 'text-muted-foreground' : 'text-foreground'
          )}
        >
          {label}
        </p>
        <div className='text-xs text-muted-foreground'>{hint}</div>
      </div>
      {children}
    </div>
  );
}

const MODE_OPTIONS = [
  { value: 'timed', label: 'Rounds' },
  { value: 'flow', label: 'Flow' },
] as const;

const backToAuto = (onClick: () => void) => (
  <>
    Set by you ·{' '}
    <button
      type='button'
      onClick={onClick}
      className='tap-target font-medium text-foreground underline underline-offset-2 hover:text-primary'
    >
      Back to auto
    </button>
  </>
);

/**
 * The session plan: how long each round is, how many, and the breaks between
 * them. Breaks follow the focus length until the student sets one; the long
 * break goes halfway, and only a session of 4 rounds or more has one.
 */
export function SessionPlanPanel({
  plan,
  onChange,
  now,
}: SessionPlanPanelProps) {
  const flow = plan.mode === 'flow';
  const resolved = sessionPlan({
    focusMinutes: plan.minutes,
    rounds: plan.rounds,
    shortMinutes: plan.shortMinutes,
    longMinutes: plan.longMinutes,
  });
  const long = resolved.longAfter !== null;
  const one = resolved.rounds === 1;
  const total = resolved.totalMs;

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between gap-3'>
        <p className='text-sm font-semibold text-foreground'>Session plan</p>
        <SegmentedControl
          label='Timer type'
          size='sm'
          options={MODE_OPTIONS}
          value={plan.mode}
          onChange={mode => onChange({ mode })}
          segmentClassName='tap-target'
        />
      </div>
      {flow ? (
        <p className='border-t border-border/70 pt-3 text-sm text-muted-foreground'>
          Flow counts up from 0:00 until you stop. The break that follows is a
          fifth of what you put in (3 to 20 min), so there is nothing to plan.
        </p>
      ) : (
        <div>
          <Row
            label='Focus'
            hint={
              // On a phone the picks are drawn bigger and further apart:
              // at 25x20 and 4px apart, 44px tap areas would cover each
              // other, so the drawn size has to grow, not only the area.
              <span className='mt-1 flex gap-1 max-sm:mt-1.5 max-sm:gap-1.5'>
                {FOCUS_PRESETS.map(m => (
                  <button
                    key={m}
                    type='button'
                    aria-pressed={plan.minutes === m}
                    onClick={() => onChange({ minutes: m })}
                    className={cn(
                      'tap-target h-5 rounded px-1.5 font-mono text-xs transition-colors duration-fast max-sm:h-8 max-sm:min-w-10',
                      plan.minutes === m
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {m}
                  </button>
                ))}
              </span>
            }
          >
            <Stepper
              label='Focus length'
              value={resolved.focusMinutes}
              unit=' min'
              min={MIN_FOCUS_MINUTES}
              max={MAX_FOCUS_MINUTES}
              step={FOCUS_STEP_MINUTES}
              onChange={minutes => onChange({ minutes })}
            />
          </Row>
          <Row label='Rounds' hint='Then the session ends'>
            <Stepper
              label='Rounds'
              value={resolved.rounds}
              min={MIN_ROUNDS}
              max={MAX_ROUNDS}
              step={1}
              onChange={rounds => onChange({ rounds })}
            />
          </Row>
          <Row
            label='Short break'
            off={one}
            hint={
              one
                ? 'No breaks in a one-round session'
                : plan.shortMinutes === null
                  ? 'Auto · a fifth of the focus time'
                  : backToAuto(() => onChange({ shortMinutes: null }))
            }
          >
            <Stepper
              label='Short break'
              value={resolved.shortMinutes}
              unit=' min'
              {...SHORT_BREAK_RANGE}
              disabled={one}
              onChange={shortMinutes => onChange({ shortMinutes })}
            />
          </Row>
          <Row
            label='Long break'
            off={!long}
            hint={
              !long
                ? 'Only in sessions of 4 rounds or more'
                : plan.longMinutes === null
                  ? `Auto · halfway, after round ${resolved.longAfter}`
                  : backToAuto(() => onChange({ longMinutes: null }))
            }
          >
            <Stepper
              label='Long break'
              value={resolved.longMinutes}
              unit=' min'
              {...LONG_BREAK_RANGE}
              disabled={!long}
              onChange={longMinutes => onChange({ longMinutes })}
            />
          </Row>
          <div className='mt-2 space-y-0.5'>
            <p className='text-sm font-semibold text-foreground'>
              {resolved.rounds} {resolved.rounds === 1 ? 'round' : 'rounds'} ·{' '}
              {formatMinutes(total)}
            </p>
            <p className='flex items-center gap-1.5 font-mono text-xs text-muted-foreground'>
              <CalendarClock
                className='h-3.5 w-3.5 shrink-0'
                aria-hidden='true'
              />
              Ends around {formatEndTime(now, now + total)}
            </p>
            {total > 4 * 60 * 60_000 && (
              <p className='text-xs text-warning-emphasis'>
                That is {formatMinutes(total)} in one sitting. Two shorter
                sessions usually go better.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
